// 🏷 **오프닝 타이틀 카드(에이전트 Remotion · 2026-09-14)** — 영상 없는 단색 카드(제목 + 교육용 고지 부제).
//
// ★캔버스 `drawTitleCard` 와 같은 비율★ — 제목 round(W×0.085)·줄높이 1.2·부제 round(W×0.045)·페이드인 0.2 구간.
//   긴 제목은 `fitTitleLines` 대신 CSS 로 같은 뜻을 낸다: 폭 84% 안에서 글자를 버리지 않고 **낱말 단위** 줄바꿈
//   (`keep-all` + `overflowWrap:anywhere` = 캔버스 `wrapWordsByMeasure` 와 같은 뜻 · 2026-09-16) · 가로 3줄·세로 4줄을 넘기면 글자 크기를 단계적으로 줄인다(하한 50%).
// ★씬이다★ — 이 카드는 `StorySegment.brandIntro` 를 든 세그(자막 자리표시자·img null·음성 무음)의 시간창에
//   그려진다. 호출부가 Sequence(from/duration)로 창을 정하고, 카드는 창 안에서 로컬 프레임만 본다.
import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";

export interface BrandIntroCardProps {
  title: string;
  subtitle?: string | null;
  bg?: string | null;
  fg?: string | null;
  /** 카드 길이(프레임) — 페이드인 비율 계산용. */
  durationInFrames: number;
}

/** 제목 글자 수와 화면비로 글자 크기 배율을 정한다(캔버스 `fitTitleLines` 의 CSS 근사 · 순수). */
export function brandIntroTitleScale(title: string, width: number, height: number): number {
  const charsPerLine = Math.max(1, Math.floor((width * 0.84) / (width * 0.085)));
  const maxLines = width > height ? 3 : 4;
  const need = Math.ceil(Math.max(1, Array.from(title).length) / charsPerLine);
  if (need <= maxLines) return 1;
  return Math.max(0.5, maxLines / need);
}

export const BrandIntroCard: React.FC<BrandIntroCardProps> = ({ title, subtitle, bg, fg, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  return <BrandIntroPaint title={title} subtitle={subtitle} bg={bg} fg={fg} durationInFrames={durationInFrames} frame={frame} width={width} height={height} />;
};

/** Pure source painter. Native callers supply original viewport and preserved source clock. */
export function BrandIntroPaint({ title, subtitle, bg, fg, durationInFrames, frame, width: W, height: H }: BrandIntroCardProps & { frame: number; width: number; height: number }) {
  const t01 = durationInFrames > 0 ? Math.min(1, Math.max(0, frame / durationInFrames)) : 1;
  const appear = Math.min(1, t01 / 0.2);
  const scale = brandIntroTitleScale(title, W, H);
  const titleSize = Math.round(W * 0.085 * scale);
  const subSize = Math.round(W * 0.045);
  const sub = (subtitle ?? "").trim();
  return (
    <div
      data-layout-slot="brandIntro"
      style={{
        position: "absolute", inset: 0,
        backgroundColor: bg || "#111827",
        color: fg || "#ffffff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center",
        fontFamily: '"Noto Sans KR", sans-serif',
        pointerEvents: "none",
      }}
    >
      <div style={{ width: "84%", fontWeight: 800, fontSize: titleSize, lineHeight: 1.2, wordBreak: "keep-all", overflowWrap: "anywhere", opacity: appear }}>
        {title}
      </div>
      {sub && (
        <div style={{ width: "84%", marginTop: titleSize * 0.45, fontWeight: 500, fontSize: subSize, lineHeight: 1.3, opacity: appear * 0.85 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
